import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { SubsidiaryVatCode, SubsidiaryVatCodeVersion, UpsertSubsidiaryVatCode } from "./types";

export const useSubsidiaryVatCodesApi = () => {
  const api = useApi();

  const getAdminSubsidiaryVatCode = useCallback(
    (subVatCodeId: string, signal: AbortSignal) => {
      const url = `/admin/subsidiary_vat_codes/${subVatCodeId}`;
      return useSWRMutation<SubsidiaryVatCode | null, CustomError, string>(url, (url) =>
        api.get<SubsidiaryVatCode>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminSubVatCodes = useCallback(
    <K extends keyof SubsidiaryVatCode>(
      query: PaginationQueryWithFields<NestedPick<SubsidiaryVatCode, K>>,
      signal: AbortSignal,
      subsidiaryId?: string,
    ) => {
      const url = buildUrl(
        subsidiaryId ? `/admin/subsidiaries/${subsidiaryId}/vat_codes` : `/admin/subsidiary_vat_codes`,
        query,
      );
      return useSWR<Paginated<NestedPick<SubsidiaryVatCode, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<SubsidiaryVatCode, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminSubVatCode = useCallback(
    async (body: UpsertSubsidiaryVatCode): Promise<{ id: string }> => {
      const url = `/admin/subsidiary_vat_codes`;
      const id = await api.post<UpsertSubsidiaryVatCode, { id: string }>(url, body);
      api.mutateCache([url, `/admin/subsidiaries/${body.subsidiaryId}/vat_codes`]);
      return id;
    },
    [api],
  );

  const updateAdminSubVatCode = useCallback(
    async (id: string, body: UpsertSubsidiaryVatCode) => {
      const url = `/admin/subsidiary_vat_codes/${id}`;
      await api.put<UpsertSubsidiaryVatCode>(url, body);
      api.mutateCache([
        url,
        `/admin/subsidiaries/${body.subsidiaryId}/vat_codes`,
        `/admin/subsidiaries/${body.subsidiaryId}/vat_codes/${id}/versions`,
        `/admin/subsidiary_vat_codes`,
        `/admin/subsidiary_vat_codes/${id}/versions`,
      ]);
    },
    [api],
  );

  const deleteAdminSubVatCode = useCallback(
    async (subVatCodeId: string, subsidiaryId: string): Promise<void> => {
      const url = `/admin/subsidiary_vat_codes/${subVatCodeId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/subsidiary_vat_codes`, `/admin/subsidiaries/${subsidiaryId}/vat_codes`]);
      });
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/subsidiary_vat_codes/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSubsidiaryVatCodeHistory = useCallback(
    (query: PaginationQuery, subVatCodeID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/subsidiary_vat_codes/${subVatCodeID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSubsidiaryVatCodeVersion = useCallback(
    (subVatCodeID: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/subsidiary_vat_codes/${subVatCodeID}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<SubsidiaryVatCodeVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<SubsidiaryVatCodeVersion>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    updateAdminSubVatCode,
    deleteAdminSubVatCode,
    insertAdminSubVatCode,
    useAdminSubVatCodes,
    getFieldFilters,
    getAdminSubsidiaryVatCode,
    getAdminSubsidiaryVatCodeHistory,
    getAdminSubsidiaryVatCodeVersion,
  };
};
